import type { SyncDryRun } from '@nemo/api';
import type { SyncRunSummary } from '@nemo/api';
import { formatWhen } from '../ui/format.js';

type MemberResolution = SyncDryRun['plan']['memberResolution'];

/** Whether a run is still going, which is what keeps the page asking. */
export function isRunning(run: SyncRunSummary | null | undefined): boolean {
  return run?.status === 'queued' || run?.status === 'running';
}

/** The status line beside the button: what the last run cost and did. */
export function summariseRun(run: SyncRunSummary | null | undefined): string {
  if (run === null || run === undefined) return 'Never synced';
  switch (run.status) {
    case 'queued':
      return 'Affinity sync queued…';
    case 'running':
      return 'Syncing Affinity…';
    case 'cancelled':
      return 'Last Affinity sync was cancelled';
    case 'failed':
      return `Affinity sync failed after ${run.callsMade} of ${run.ceiling} calls: ${run.error ?? 'no reason given'}`;
    case 'done': {
      const written =
        run.relationshipsWritten === 1
          ? '1 relationship'
          : `${run.relationshipsWritten} relationships`;
      // "n of N" rather than a bare count: what a run cost only means anything
      // beside what it was allowed to spend.
      const calls = `${run.callsMade} of ${run.ceiling} calls`;
      return `${calls}, ${run.fundsMatched} funds matched, ${written} written ${formatWhen(run.finishedAt)}`;
    }
  }
}

/** The one line a dry run is worth before anybody expands it. */
export function summariseDryRun(dryRun: SyncDryRun): string {
  const calls = dryRun.callsMade === 1 ? '1 call' : `${dryRun.callsMade} calls`;
  const ceiling = dryRun.plan.stoppedAtCeiling
    ? ` (stopped at the ${dryRun.maxCalls} ceiling)`
    : '';
  const readErrors =
    dryRun.readErrors.length === 0 ? '' : `, ${dryRun.readErrors.length} read errors`;
  return (
    `${dryRun.mode} walk, ${calls}${ceiling}: ${dryRun.fundsMatched} funds, ` +
    `${dryRun.relationshipsWouldWrite} relationships and ${dryRun.investorsWouldCreate} people ` +
    `would be written, ${dryRun.ambiguousPeople.length} names could not be decided${readErrors}`
  );
}

/**
 * The same person, held several times. An Affinity workspace captures somebody
 * afresh every time an import or an inbox finds them, and each copy carries
 * its own relationships — so a person reading the plan needs to know that the
 * one row on the list stands for three records over there.
 */
function describeRecords(person: SyncDryRun['plan']['funds'][number]['people'][number]): string {
  return person.duplicateIds.length === 0
    ? ''
    : `, ${person.duplicateIds.length + 1} records in Affinity`;
}

/** "members resolved 9 of 13", ahead of what the run did with any of them. */
export function summariseMemberResolution(resolution: MemberResolution): string {
  return `members resolved ${resolution.resolvedCount} of ${resolution.total}`;
}

/**
 * One line per member this run could not place in Affinity — "no email on
 * file" for a member with no address to search, otherwise the address the
 * search tried, so the gap names what to fix rather than just that one
 * exists.
 */
export function describeUnresolvedMembers(resolution: MemberResolution): string[] {
  return resolution.members
    .filter((member) => !member.resolved)
    .map((member) =>
      member.memberEmail === null
        ? `${member.memberName} — no email on file`
        : `${member.memberName} — not found (searched ${member.memberEmail})`,
    );
}

/** A read that failed for this person without stopping the run. */
function describeReadErrors(person: SyncDryRun['plan']['funds'][number]['people'][number]): string {
  if (person.readErrors.length === 0) return '';
  const noun = person.readErrors.length === 1 ? 'read error' : 'read errors';
  return `, ${person.readErrors.length} ${noun}`;
}

/** What a dry run would do about one person, in a person's words. */
export function describePlannedPerson(
  person: SyncDryRun['plan']['funds'][number]['people'][number],
): string {
  const who = person.affinityName ?? person.investorName ?? 'somebody';
  const outcome = ((): string => {
    switch (person.outcome) {
      case 'matched': {
        const renamed = person.renamedTo === null ? '' : `, renamed to ${person.renamedTo}`;
        return `${who} is ${person.investorName ?? 'ours'}${renamed}${describeRecords(person)}`;
      }
      case 'created':
        return `${who} would be added${describeRecords(person)}`;
      case 'excluded':
        return `${who} dropped — ${person.title ?? 'title'}`;
      case 'ambiguous':
        return `${who} could be ${person.candidateNames.join(' or ')}`;
      case 'absent':
        return `${who} is not in Affinity at this fund`;
    }
  })();
  return `${outcome}${describeReadErrors(person)}`;
}
